import { useState, useRef, useEffect } from 'react';
import { Play } from 'lucide-react';
import { cn } from '@/lib/utils';

interface LazyVideoProps {
  src: string;
  className?: string;
}

const LazyVideo = ({ src, className }: LazyVideoProps) => {
  const [isInView, setIsInView] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [posterUrl, setPosterUrl] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Generate poster from first frame using an off-screen video + canvas
  useEffect(() => {
    if (!isInView || isPlaying) return;

    const offVideo = document.createElement('video');
    offVideo.crossOrigin = 'anonymous';
    offVideo.muted = true;
    offVideo.playsInline = true;
    offVideo.preload = 'auto';
    offVideo.src = src;

    const handleSeeked = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = offVideo.videoWidth || 640;
        canvas.height = offVideo.videoHeight || 360;
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.drawImage(offVideo, 0, 0, canvas.width, canvas.height);
          setPosterUrl(canvas.toDataURL('image/jpeg', 0.8));
        }
      } catch {
        // CORS or other error – thumbnail stays null
      }
      offVideo.remove();
    };

    offVideo.addEventListener('seeked', handleSeeked, { once: true });
    offVideo.addEventListener('loadeddata', () => {
      offVideo.currentTime = 0.1;
    }, { once: true });

    return () => {
      offVideo.removeEventListener('seeked', handleSeeked);
      offVideo.pause();
      offVideo.src = '';
      offVideo.remove();
    };
  }, [isInView, src, isPlaying]);

  const handlePlay = () => {
    setIsPlaying(true);
    setTimeout(() => videoRef.current?.play(), 50);
  };

  return (
    <div ref={containerRef} className={cn('relative bg-foreground/5 overflow-hidden', className)}>
      {isInView && (
        <>
          {isPlaying ? (
            <video
              ref={videoRef}
              src={src}
              controls
              playsInline
              className="w-full h-full object-cover"
            />
          ) : (
            <>
              {posterUrl && (
                <img
                  src={posterUrl}
                  alt=""
                  className="w-full h-full object-cover"
                />
              )}
              <button
                onClick={handlePlay}
                className="absolute inset-0 w-full h-full flex items-center justify-center bg-foreground/20 hover:bg-foreground/30 transition-colors cursor-pointer group z-10"
                aria-label="Play video"
              >
                <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                  <Play className="w-7 h-7 text-primary-foreground ml-1" />
                </div>
              </button>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default LazyVideo;
